import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import { Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { rotatePlugin } from '@react-pdf-viewer/rotate';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInvoiceController } from 'api/controllers/InvoiceController';

import { EntryType, Invoice, InvoicePatch, InvoiceSection } from 'openapi';

import { Approvals } from 'components/forms/InvoiceVerificationForms/Approvals';
import { AdditionalInvoiceFields } from 'components/forms/InvoiceVerificationForms/ReceiverForm';
import { PdfViewerWithWatermark } from 'components/PdfViewerWithWatermark/PdfViewerWithWatermark';
import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import { getConfigByDocumentType } from 'utils/config/documentEntries';
import { PDF_JS_WORKER_URL } from 'utils/constants/constants';
import { InvoiceSectionKey } from 'utils/interfaces/InvoiceProps';

import {
  verificationContentWrapper,
  zoomButtonsWrapper
} from 'styles/pages/InvoiceVerificationStyle';

interface FileInvoiceApprovalProps {
  currentInvoice: Invoice;
  setCurrentInvoice: React.Dispatch<React.SetStateAction<Invoice | null>>;
  refreshInvoice: (newCompanyId: number) => Promise<void>;
}

export const FileInvoiceApproval = ({
  currentInvoice,
  setCurrentInvoice,
  refreshInvoice
}: FileInvoiceApprovalProps) => {
  const {
    getInvoiceFile,
    approveInvoicesByIds,
    rejectInvoicesByIds,
    settleInvoices,
    returnToValidatedById,
    saveInvoiceData,
    unverifyInvoiceById
  } = useInvoiceController();

  const { translate } = useTranslations();

  const [pdfFileUrl, setPdfFileUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const documentConfig = useMemo(
    () => getConfigByDocumentType(currentInvoice.entryType as EntryType),
    [currentInvoice.entryType]
  );

  const isPdf = useMemo(
    () => Boolean(currentInvoice.filePath?.endsWith('.pdf')),
    [currentInvoice.filePath]
  );

  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  const rotatePluginInstance = rotatePlugin();
  const { RotateBackwardButton, RotateForwardButton } = rotatePluginInstance;

  const fetchInvoiceFile = useCallback(async () => {
    setIsLoading(true);
    try {
      const fileData = await getInvoiceFile(Number(currentInvoice.id));
      setPdfFileUrl(fileData.url);
    } finally {
      setIsLoading(false);
    }
  }, [currentInvoice.id, getInvoiceFile]);

  useEffect(() => {
    fetchInvoiceFile();
  }, [fetchInvoiceFile]);

  const handleDownloadInvoice = useCallback(async () => {
    if (!pdfFileUrl) return;
    const link = document.createElement('a');
    link.href = pdfFileUrl;
    link.download = currentInvoice.filePath || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [pdfFileUrl, currentInvoice.filePath]);

  const handleApprove = useCallback(async () => {
    const result = await approveInvoicesByIds([Number(currentInvoice.id)]);
    setCurrentInvoice(result);
  }, [approveInvoicesByIds, currentInvoice.id, setCurrentInvoice]);

  const handleSettle = useCallback(async () => {
    const result = await settleInvoices({ ids: [Number(currentInvoice.id)] });
    setCurrentInvoice(result);
  }, [currentInvoice.id, settleInvoices, setCurrentInvoice]);

  const handleReject = useCallback(
    async (reason: string) => {
      await rejectInvoicesByIds([Number(currentInvoice.id!)], reason);
      if (currentInvoice.companyId) {
        await refreshInvoice(currentInvoice.companyId);
      }
    },
    [
      currentInvoice.id,
      currentInvoice.companyId,
      rejectInvoicesByIds,
      refreshInvoice
    ]
  );

  const handleReturnToValidated = useCallback(async () => {
    if (!currentInvoice.companyId || !currentInvoice.id) return;
    const result = await returnToValidatedById(
      currentInvoice.companyId,
      currentInvoice.id
    );
    setCurrentInvoice(result);
  }, [
    currentInvoice.companyId,
    currentInvoice.id,
    returnToValidatedById,
    setCurrentInvoice
  ]);

  const handleReturnToDraft = useCallback(async () => {
    if (!currentInvoice.companyId || !currentInvoice.id) return;
    await unverifyInvoiceById(
      currentInvoice.companyId,
      currentInvoice.id,
      InvoiceSection.DOCUMENT_DATA
    );
    await refreshInvoice(currentInvoice.companyId);
  }, [
    currentInvoice.companyId,
    currentInvoice.id,
    refreshInvoice,
    unverifyInvoiceById
  ]);

  const handleSaveData = useCallback(
    async (
      values: InvoicePatch & AdditionalInvoiceFields,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _section: InvoiceSectionKey
    ) => {
      if (!currentInvoice.companyId || !currentInvoice.id) return;
      await saveInvoiceData(
        currentInvoice.companyId,
        currentInvoice.id,
        values
      );
      showSuccessToastMessage(translate('messages.successfullyUpdated'));
      await refreshInvoice(currentInvoice.companyId);
    },
    [
      currentInvoice.companyId,
      currentInvoice.id,
      saveInvoiceData,
      refreshInvoice,
      translate
    ]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...verificationContentWrapper,
        display: 'flex',
        gap: { xs: 7, lg: 2 },
        flexDirection: {
          lg: 'row',
          xs: 'column-reverse'
        },
        justifyContent: 'space-between',
        height: 'calc(100vh - 200px)',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          width: { lg: '50%', xs: '100%' },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {pdfFileUrl && isPdf && (
          <Worker workerUrl={PDF_JS_WORKER_URL || ''}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <PdfViewerWithWatermark
                  fileUrl={pdfFileUrl}
                  plugins={[zoomPluginInstance, rotatePluginInstance]}
                  watermarkText={currentInvoice.stage as string}
                />
              </Box>
              <Box sx={zoomButtonsWrapper}>
                <RotateBackwardButton />
                <ZoomOutButton />
                <ZoomPopover />
                <ZoomInButton />
                <RotateForwardButton />
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDownloadInvoice}
                >
                  <FileDownloadIcon />
                </Button>
              </Box>
            </Box>
          </Worker>
        )}

        {pdfFileUrl && !isPdf && (
          <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
            <img
              src={pdfFileUrl}
              alt="Preview invoice"
              style={{
                width: '100%',
                height: 'auto'
              }}
            />
          </Box>
        )}

        {!pdfFileUrl && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: { lg: '50%', xs: '100%' },
          height: '100%',
          overflow: 'auto'
        }}
      >
        <Approvals
          documentConfig={documentConfig}
          currentInvoice={currentInvoice}
          handleApprove={handleApprove}
          handleSettle={handleSettle}
          handleReject={handleReject}
          handleReturnToValidated={handleReturnToValidated}
          handleBack={handleReturnToDraft}
          handleBackFromMiddleStep={() => {}}
          handleSaveData={handleSaveData}
        />
      </Box>
    </Box>
  );
};
